import { SystemTypes } from 'spark-core-dx/services';

export const CLIENT_ONBOARDING_ITEM_NAMES = Object.freeze({
  run: 'ClientOnboardingRun',
  wizard: 'ClientOnboardingWizard',
});

export const CLIENT_ONBOARDING_OPERATIONS = Object.freeze({
  start: 'StartClientOnboarding',
  load: 'LoadClientOnboarding',
  loadSecurityOptions: 'LoadClientOnboardingSecurityOptions',
  validate: 'ValidateClientOnboarding',
  execute: 'ExecuteClientOnboarding',
  resume: 'ResumeClientOnboarding',
  retry: 'RetryClientOnboardingStep',
  cancel: 'CancelClientOnboarding',
});

export const CLIENT_ONBOARDING_NEW_RUN_CARD_KEY = 'new-client-onboarding';

export const CLIENT_ONBOARDING_AUTHENTICATION = Object.freeze({
  entra: 'Microsoft Entra ID',
  guest: 'Guest / External Access',
  entraAccessMode: 'Read / Login',
  guestAccessMode: 'Hosted Guest',
});

export const CLIENT_ONBOARDING_AUTHENTICATION_OPTIONS_PROPERTY =
  'AuthenticationProviderOptions';

export const CLIENT_ONBOARDING_AUTHENTICATION_OPTIONS = Object.freeze([
  Object.freeze({
    Text: CLIENT_ONBOARDING_AUTHENTICATION.entra,
    Value: CLIENT_ONBOARDING_AUTHENTICATION.entra,
  }),
  Object.freeze({
    Text: CLIENT_ONBOARDING_AUTHENTICATION.guest,
    Value: CLIENT_ONBOARDING_AUTHENTICATION.guest,
  }),
]);

const RESERVED_ONBOARDING_ROLE_CODES = new Set([
  'systemadmin',
  'anonymous',
  'basic',
]);

export const normalizeOnboardingRoleCodes = (roleCodes = []) => {
  const seen = new Set();
  return (Array.isArray(roleCodes) ? roleCodes : []).reduce(
    (normalized, roleCode) => {
      const code = String(roleCode || '').trim();
      const key = code.toLowerCase();
      if (!code || RESERVED_ONBOARDING_ROLE_CODES.has(key) || seen.has(key)) {
        return normalized;
      }
      seen.add(key);
      normalized.push(code);
      return normalized;
    },
    [],
  );
};

export const CLIENT_ONBOARDING_STEP_KEYS = Object.freeze([
  'client-shell',
  'application-options',
  'client-configuration-overrides',
  'domains',
  'initial-client-administrator',
  'security-permission-baseline',
  'database-partition',
  'review-validate',
  'create-initialize-verify',
]);

export const CLIENT_ONBOARDING_FORM_FIELDS = Object.freeze({
  'client-shell': Object.freeze([
    'Name',
    'DisplayName',
    'ShortName',
    'ClientIdentifier',
    'EIN',
    'FirstName',
    'LastName',
    'Email',
    'PrimaryPhone',
    'SecondaryPhone',
    'PrimaryAddress',
    'SecondaryAddress',
    'MarketplaceAccessMode',
  ]),
  'client-configuration-overrides': Object.freeze([
    'AuthenticationProvider',
    'EntraTenantId',
    'EntraTenantDomain',
    'EntraReadClientId',
    'EntraGraphReadClientId',
    'EntraGraphReadClientSecretInput',
  ]),
  'initial-client-administrator': Object.freeze([
    'InitialAdministratorFirstName',
    'InitialAdministratorLastName',
    'InitialAdministratorEmail',
    'InitialAdministratorUserName',
    'InitialAdministratorApplicationIds',
    'InitialAdministratorRoleCodes',
    'InvitationMethod',
    'InvitationTiming',
  ]),
  'security-permission-baseline': Object.freeze([]),
  'database-partition': Object.freeze([
    'PartitionMode',
    'PartitionConnectionStrategy',
    'PartitionDedicatedMongoDbConnectionString',
    'PartitionMigrationStrategy',
  ]),
  'review-validate': Object.freeze([
    'ConfirmExistingClientReconciliation',
  ]),
});

export const isGuestOnboardingAuthentication = (provider) => (
  String(provider || '').trim()
  === CLIENT_ONBOARDING_AUTHENTICATION.guest
);

export const getAuthenticationFieldNames = (provider) => (
  isGuestOnboardingAuthentication(provider)
    ? ['AuthenticationProvider']
    : CLIENT_ONBOARDING_FORM_FIELDS['client-configuration-overrides']
);

export const applyAuthenticationProviderSelection = (run, provider) => {
  if (!run) return run;
  const selectedProvider = isGuestOnboardingAuthentication(provider)
    ? CLIENT_ONBOARDING_AUTHENTICATION.guest
    : CLIENT_ONBOARDING_AUTHENTICATION.entra;
  if (selectedProvider !== CLIENT_ONBOARDING_AUTHENTICATION.guest) {
    return {
      ...run,
      AuthenticationProvider: selectedProvider,
      AuthenticationAccessMode:
        CLIENT_ONBOARDING_AUTHENTICATION.entraAccessMode,
    };
  }
  return {
    ...run,
    AuthenticationProvider: selectedProvider,
    AuthenticationAccessMode:
      CLIENT_ONBOARDING_AUTHENTICATION.guestAccessMode,
    EntraTenantId: null,
    EntraTenantDomain: null,
    EntraReadClientId: null,
    EntraGraphReadClientId: null,
    EntraGraphReadClientSecretInput: null,
    EntraGroupRoleMappings: [],
  };
};

export const buildAuthenticationFormData = (run) => (
  !run
    ? run
    : {
      ...run,
      [CLIENT_ONBOARDING_AUTHENTICATION_OPTIONS_PROPERTY]:
        CLIENT_ONBOARDING_AUTHENTICATION_OPTIONS.map((option) => ({
          ...option,
        })),
    }
);

export const CLIENT_ONBOARDING_DOMAIN_METADATA = Object.freeze({
  Name: 'ClientOnboardingDomain',
  DisplayName: 'Client Domain',
  Fields: Object.freeze([
    {
      FieldName: 'RegisteredAppId',
      DisplayName: 'Global Application',
      Description: 'Selects the registered application this address opens. The application, URL, and Current Environment together identify the client route; this does not assign the application to the client.',
      DataOperation: 'SelectDataClientOnboardingApplications',
      FieldType: 25,
      DataOperationApp: 'Portal',
      DisplaySequence: 1,
      GridSequence: 1,
      ShowOnGrid: true,
      IsRequired: true,
    },
    {
      FieldName: 'Domain',
      DisplayName: 'Full Domain URL',
      Description: 'Complete application address, including http:// or https://, used to redirect this client in the Current Environment. The same application, URL, and environment combination can belong to only one client.',
      FieldType: 1,
      DisplaySequence: 2,
      GridSequence: 2,
      ShowOnGrid: true,
      IsRequired: true,
    },
    {
      FieldName: 'OriginTenantId',
      DisplayName: 'Origin Tenant ID',
      Description: 'Optional Microsoft Entra tenant ID used during first-time sign-in to identify this client from the token issuer. Leave blank when users authenticate from the platform default tenant.',
      FieldType: 1,
      DisplaySequence: 3,
      GridSequence: 3,
      ShowOnGrid: false,
      IsRequired: false,
    },
    {
      FieldName: 'OriginTenantDomain',
      DisplayName: 'Origin Tenant Domain',
      Description: 'Optional readable Microsoft Entra tenant domain stored with this route for administration and support. First-time sign-in matches the client by Origin Tenant ID, not this domain.',
      FieldType: 1,
      DisplaySequence: 4,
      GridSequence: 4,
      ShowOnGrid: false,
      IsRequired: false,
    },
  ]),
});

const asSelectOptions = (items, textResolver, valueResolver) => JSON.stringify(
  (Array.isArray(items) ? items : []).map((item) => ({
    Text: textResolver(item),
    Value: valueResolver(item),
  })),
);

export const buildOnboardingGroupRoleMetadata = ({
  groups = [],
  roles = [],
} = {}) => ({
  Name: 'ClientOnboardingEntraGroupRoleMapping',
  DisplayName: 'Entra Group Role Mapping',
  Fields: [
    {
      FieldName: 'EntraGroupId',
      DisplayName: 'Client Entra Group',
      Description: 'Security-enabled group loaded from the configured client Microsoft Entra directory. The immutable Entra group object ID is saved for login matching.',
      FieldType: SystemTypes.UIFieldType.SearchSelectList,
      DataJson: asSelectOptions(
        groups,
        (group) => group.DisplayName || group.GroupId,
        (group) => group.GroupId,
      ),
      DisplaySequence: 1,
      GridSequence: 1,
      ShowOnGrid: true,
      IsRequired: true,
    },
    {
      FieldName: 'RoleCodes',
      DisplayName: 'Convoke Roles',
      Description: 'Select one or more current-application roles granted when a signed-in user belongs to this Entra group. Reserved system roles are not available.',
      FieldType: SystemTypes.UIFieldType.MultiSelectList,
      DataJson: asSelectOptions(
        roles,
        (role) => role.DisplayName || role.RoleCode,
        (role) => role.RoleCode,
      ),
      DisplaySequence: 2,
      GridSequence: 2,
      ShowOnGrid: true,
      IsRequired: true,
    },
  ],
});

export const CLIENT_ONBOARDING_GROUP_ROLE_CARD_METADATA = Object.freeze({
  Name: 'ClientOnboardingEntraGroupRoleMappingSummary',
  DisplayName: 'Entra Group Role Mappings',
  Fields: Object.freeze([
    {
      FieldName: 'EntraGroupDisplayName',
      DisplayName: 'Client Entra Group',
      PrimaryField: true,
      GridSequence: 1,
      DisplaySequence: 1,
      ShowOnGrid: true,
    },
    {
      FieldName: 'RoleDisplayNames',
      DisplayName: 'Convoke Roles',
      GridSequence: 2,
      DisplaySequence: 2,
      ShowOnGrid: true,
    },
  ]),
});

export const CLIENT_ONBOARDING_RUN_CARD_METADATA = Object.freeze({
  Name: 'ClientOnboardingRunSummary',
  DisplayName: 'Client Onboarding',
  Fields: Object.freeze([
    {
      FieldName: 'ClientName',
      DisplayName: 'Client',
      PrimaryField: true,
      GridSequence: 1,
      DisplaySequence: 1,
      ShowOnGrid: true,
    },
    {
      FieldName: 'RunState',
      DisplayName: 'State',
      GridSequence: 2,
      DisplaySequence: 2,
      ShowOnGrid: true,
    },
    {
      FieldName: 'Progress',
      DisplayName: 'Progress',
      GridSequence: 3,
      DisplaySequence: 3,
      ShowOnGrid: true,
    },
    {
      FieldName: 'LastSavedAtUtc',
      DisplayName: 'Last Saved',
      GridSequence: 4,
      DisplaySequence: 4,
      ShowOnGrid: true,
    },
  ]),
});

export const buildOnboardingRunCards = (availableRuns = []) => [
  {
    OnboardingRunId: CLIENT_ONBOARDING_NEW_RUN_CARD_KEY,
    ClientName: 'Onboard a New Client',
    RunState: 'New',
    Progress: 'Not started',
    LastSavedAtUtc: null,
    Description: 'Start a new durable client onboarding run.',
    IsNewRun: true,
  },
  ...(Array.isArray(availableRuns) ? availableRuns : [])
    .filter((run) => !['Completed', 'Cancelled'].includes(run?.RunState))
    .map((run) => ({
      ...run,
      ClientName: String(run.ClientName || '').trim(),
      Progress: `Step ${Number(run.CurrentWizardStep || 1)} of ${CLIENT_ONBOARDING_STEP_KEYS.length}`,
      Description: `Resume the ${run.RunState || 'Draft'} onboarding run for ${run.ClientName || 'this client'}.`,
      IsNewRun: false,
    })),
];

export const getOnboardingRunCardTitle = (run) => {
  const clientName = String(run?.ClientName || '').trim();
  if (clientName) return clientName;
  if (run?.IsNewRun) return 'Onboard a New Client';
  return String(run?.RunState || '').trim() || 'Onboarding Draft';
};

const getFieldName = (field) => field?.FieldName ?? field?.fieldName;

export const CLIENT_IDENTIFIER_DESCRIPTION = 'Optional unique identifier used to match this client to the same customer or account in an external system. If left blank, the created Client record uses Short Name as its client identifier.';

export const AUTHENTICATION_FIELD_PRESENTATION = Object.freeze({
  AuthenticationProvider: Object.freeze({
    DisplayName: 'Default Authentication',
    Description: 'Choose customer-owned Microsoft Entra ID or Convoke-hosted Guest / External Access.',
    IsRequired: true,
  }),
  EntraTenantId: Object.freeze({
    DisplayName: 'Customer Entra Tenant ID',
    Description: 'Directory tenant identifier owned by the client.',
    IsRequired: true,
  }),
  EntraTenantDomain: Object.freeze({
    DisplayName: 'Customer Entra Tenant Domain',
    Description: 'Initial verified Microsoft Entra domain, such as contoso.onmicrosoft.com.',
    IsRequired: true,
  }),
  EntraReadClientId: Object.freeze({
    DisplayName: 'Sign-in Application Client ID',
    Description: 'Application registration used for interactive user sign-in.',
    IsRequired: true,
  }),
  EntraGraphReadClientId: Object.freeze({
    DisplayName: 'Graph Read Application Client ID',
    Description: 'Least-privilege application used only for required directory reads.',
    IsRequired: true,
  }),
  EntraGraphReadClientSecretInput: Object.freeze({
    DisplayName: 'Graph Read Client Secret',
    Description: 'Write-only. Leave blank to preserve the protected credential.',
    IsRequired: false,
  }),
});

const replaceFieldDescriptions = (metadata, descriptions) => {
  const fieldKey = Array.isArray(metadata?.Fields) ? 'Fields' : 'fields';
  const fields = metadata?.[fieldKey] ?? [];
  return {
    ...(metadata || {}),
    [fieldKey]: fields.map((field) => {
      const description = descriptions[getFieldName(field)];
      return description
        ? {
          ...field,
          Description: description,
          description,
        }
        : field;
    }),
  };
};

export const buildClientInformationMetadata = (metadata) => replaceFieldDescriptions(
  metadata,
  { ClientIdentifier: CLIENT_IDENTIFIER_DESCRIPTION },
);

export const buildAuthenticationMetadata = (metadata) => replaceFieldDescriptions(
  {
    ...(metadata || {}),
    [Array.isArray(metadata?.Fields) ? 'Fields' : 'fields']:
      (metadata?.Fields ?? metadata?.fields ?? []).map((field) => {
        const fieldName = getFieldName(field);
        const presentation = AUTHENTICATION_FIELD_PRESENTATION[fieldName];
        return presentation
          ? {
            ...field,
            DisplayName: presentation.DisplayName,
            displayName: presentation.DisplayName,
            IsRequired: presentation.IsRequired,
            isRequired: presentation.IsRequired,
            ...(fieldName === 'AuthenticationProvider' ? {
              DataJson: CLIENT_ONBOARDING_AUTHENTICATION_OPTIONS.map(
                (option) => ({ ...option }),
              ),
              DataOperation: null,
              DataOperationItem: null,
              DataProperty:
                CLIENT_ONBOARDING_AUTHENTICATION_OPTIONS_PROPERTY,
            } : {}),
          }
          : field;
      }),
  },
  Object.fromEntries(Object.entries(AUTHENTICATION_FIELD_PRESENTATION)
    .map(([fieldName, presentation]) => [
      fieldName,
      presentation.Description,
    ])),
);

export const selectMetadataFields = (metadata, fieldNames = []) => {
  const accepted = new Set(fieldNames);
  const fields = metadata?.Fields ?? metadata?.fields ?? [];
  return fields.filter((field) => accepted.has(getFieldName(field)));
};

export const buildInitialAdministratorMetadata = (
  metadata,
  roles = [],
) => {
  const assignableRoles = (Array.isArray(roles) ? roles : []).filter(
    (role) => normalizeOnboardingRoleCodes([role?.RoleCode]).length === 1,
  );
  const optionJson = asSelectOptions(
    assignableRoles,
    (role) => role.DisplayName || role.RoleCode,
    (role) => role.RoleCode,
  );
  const roleField = {
    FieldName: 'InitialAdministratorRoleCodes',
    DisplayName: 'Initial Administrator Roles',
    Description: 'Select one or more assignable roles in the current application. These roles are granted to the initial administrator when the profile is reconciled.',
    TabId: 'Roles|5',
    FieldType: SystemTypes.UIFieldType.MultiSelectList,
    DataJson: optionJson,
    DisplaySequence: 6,
    IsRequired: true,
  };
  const fieldKey = Array.isArray(metadata?.Fields) ? 'Fields' : 'fields';
  const fields = metadata?.[fieldKey] ?? [];
  const hasRoleField = fields.some(
    (field) => getFieldName(field) === roleField.FieldName,
  );
  const invitationIndex = fields.findIndex(
    (field) => getFieldName(field) === 'InvitationMethod',
  );
  const fieldsWithRole = hasRoleField
    ? fields.map((field) => (
      getFieldName(field) === roleField.FieldName
        ? { ...field, ...roleField }
        : field
    ))
    : invitationIndex >= 0
      ? [
        ...fields.slice(0, invitationIndex),
        roleField,
        ...fields.slice(invitationIndex),
      ]
      : [...fields, roleField];
  return {
    ...(metadata || {}),
    [fieldKey]: fieldsWithRole,
  };
};

const cleanDefaultValue = (value) => String(value || '').trim();

export const applyInitialAdministratorDefaults = (run) => {
  if (!run) return run;
  const initialEmail = cleanDefaultValue(
    run.InitialAdministratorEmail,
  ) || cleanDefaultValue(run.Email);
  const currentApplicationId = cleanDefaultValue(
    run.ConfigurationApplicationId,
  );
  const existingApplicationIds = Array.isArray(
    run.InitialAdministratorApplicationIds,
  )
    ? run.InitialAdministratorApplicationIds
      .map(cleanDefaultValue)
      .filter(Boolean)
    : [];
  const applicationIds = [
    ...new Set([
      ...existingApplicationIds,
      ...(currentApplicationId ? [currentApplicationId] : []),
    ]),
  ];
  const defaults = {
    InitialAdministratorFirstName: cleanDefaultValue(
      run.InitialAdministratorFirstName,
    ) || cleanDefaultValue(run.FirstName),
    InitialAdministratorLastName: cleanDefaultValue(
      run.InitialAdministratorLastName,
    ) || cleanDefaultValue(run.LastName),
    InitialAdministratorEmail: initialEmail,
    InitialAdministratorUserName: cleanDefaultValue(
      run.InitialAdministratorUserName,
    ) || initialEmail,
    InitialAdministratorApplicationIds: applicationIds,
    InitialAdministratorRoleCodes: normalizeOnboardingRoleCodes(
      run.InitialAdministratorRoleCodes,
    ),
  };
  const hasChanges = Object.entries(defaults).some(([key, value]) => (
    Array.isArray(value)
      ? JSON.stringify(value)
        !== JSON.stringify(run[key] || [])
      : value !== cleanDefaultValue(run[key])
  ));
  return hasChanges ? { ...run, ...defaults } : run;
};

export const createIdempotencyKey = (operationName) => {
  const random = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `client-onboarding:${operationName}:${random}`;
};

export const buildOnboardingOperationRequest = ({
  wizard,
  run,
  operationName,
  currentEnvironment,
  requestedStepKey = null,
}) => ({
  OnboardingRunId: wizard?.OnboardingRunId || null,
  ExpectedRunVersion: wizard?.ExpectedRunVersion || run?.RunVersion || null,
  IdempotencyKey: createIdempotencyKey(operationName),
  RequestedStepKey: requestedStepKey,
  CurrentEnvironment: currentEnvironment,
  Run: applyInitialAdministratorDefaults(run),
});

export const normalizeWizardSteps = (steps = []) => {
  const byKey = new Map(
    (Array.isArray(steps) ? steps : [])
      .filter((step) => CLIENT_ONBOARDING_STEP_KEYS.includes(step?.Key))
      .map((step) => [step.Key, step]),
  );
  return CLIENT_ONBOARDING_STEP_KEYS.map((key, index) => (
    byKey.get(key) || {
      Key: key,
      Order: index + 1,
      DisplayName: key,
      Description: '',
      Ownership: '',
      Status: 'Pending',
    }
  ));
};

const readFirstContextValue = (source, ...fieldNames) => {
  if (!source) return '';
  for (const fieldName of fieldNames) {
    const matchingKey = Object.keys(source).find(
      (key) => key.toLowerCase() === fieldName.toLowerCase(),
    );
    const value = matchingKey ? source[matchingKey] : undefined;
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
};

const readAuthProviderTenantId = (...sources) => {
  for (const source of sources) {
    const options = source?.AuthProviderOptions
      || source?.authProviderOptions
      || [];
    for (const provider of Array.isArray(options) ? options : []) {
      const tenantId = readFirstContextValue(
        provider,
        'TenantId',
        'AZTenantId',
        'AZTenant',
      );
      if (tenantId) return tenantId;
    }
  }
  return '';
};

export const resolveCurrentDomainTenantContext = ({
  coreContext,
  currentOrigin = globalThis.location?.origin,
  signedInTenantDomain,
  signedInTenantId,
} = {}) => {
  const profile = coreContext?.Profile || coreContext?.profile;
  const config = coreContext?.Config || coreContext?.config;
  const defaultTenantId = readFirstContextValue(
    coreContext,
    'CurrentTenantId',
  ) || readFirstContextValue(
    config,
    'AZTenant',
    'AZTenantId',
    'TenantId',
  ) || readFirstContextValue(
    coreContext,
    'AZTenant',
    'AZTenantId',
  );
  const currentTenantId = String(signedInTenantId || '').trim()
    || readFirstContextValue(
    profile,
    'TenantId',
  ) || readAuthProviderTenantId(coreContext, profile);
  const currentTenantDomain = String(signedInTenantDomain || '').trim()
    || readFirstContextValue(
    coreContext,
    'CurrentTenantDomain',
    'TenantDomain',
  ) || readFirstContextValue(
    profile,
    'CurrentTenantDomain',
    'TenantDomain',
  );
  const usesDefaultTenant = Boolean(
    currentTenantId
    && defaultTenantId
    && currentTenantId.toLowerCase() === defaultTenantId.toLowerCase(),
  );
  return {
    currentOrigin: String(currentOrigin || '').trim().replace(/\/+$/, ''),
    currentTenantId,
    currentTenantDomain,
    defaultTenantId,
    usesDefaultTenant,
  };
};

export const buildCurrentDomainTenantDraft = ({
  domains = [],
  coreContext,
  currentEnvironment,
  currentOrigin,
  registeredAppId,
  signedInTenantDomain,
  signedInTenantId,
} = {}) => {
  const context = resolveCurrentDomainTenantContext({
    coreContext,
    currentOrigin,
    signedInTenantDomain,
    signedInTenantId,
  });
  const appId = String(registeredAppId || '').trim();
  if (!context.currentOrigin || !appId) return null;
  const existing = (Array.isArray(domains) ? domains : []).find((domain) => (
    String(domain?.RegisteredAppId || '').trim().toLowerCase()
      === appId.toLowerCase()
    && String(domain?.Domain || '').trim().replace(/\/+$/, '').toLowerCase()
      === context.currentOrigin.toLowerCase()
  ));
  return normalizeDomainDraft({
    ...(existing || {}),
    RegisteredAppId: appId,
    Domain: context.currentOrigin,
    OriginTenantId: context.usesDefaultTenant
      ? null
      : context.currentTenantId || null,
    OriginTenantDomain: context.usesDefaultTenant
      ? null
      : context.currentTenantDomain || null,
  }, currentEnvironment, appId);
};

export const normalizeDomainDraft = (
  domain = {},
  currentEnvironment,
  defaultRegisteredAppId = '',
) => ({
  ...domain,
  UniqueId: domain.UniqueId || globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  RegisteredAppId: String(
    domain.RegisteredAppId || defaultRegisteredAppId || '',
  ).trim(),
  Domain: String(domain.Domain || '').trim().replace(/\/+$/, ''),
  OriginTenantId: String(domain.OriginTenantId || '').trim() || null,
  OriginTenantDomain: String(domain.OriginTenantDomain || '').trim() || null,
  Environment: currentEnvironment,
});

export const upsertDomain = (domains = [], domain, currentEnvironment) => {
  const normalized = normalizeDomainDraft(domain, currentEnvironment);
  const key = String(normalized.UniqueId);
  const existing = Array.isArray(domains) ? domains : [];
  const index = existing.findIndex((item) => String(item.UniqueId) === key);
  if (index < 0) {
    return [...existing, normalized];
  }
  return existing.map((item, itemIndex) => (
    itemIndex === index ? normalized : item
  ));
};

export const removeDomain = (domains = [], domainId) => (
  (Array.isArray(domains) ? domains : [])
    .filter((domain) => String(domain.UniqueId) !== String(domainId))
);

export const normalizeGroupRoleMappingDraft = (
  mapping = {},
  groups = [],
) => {
  const groupId = String(mapping.EntraGroupId || '').trim();
  const group = (Array.isArray(groups) ? groups : []).find(
    (option) => String(option.GroupId) === groupId,
  );
  return {
    ...mapping,
    UniqueId: mapping.UniqueId || globalThis.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    EntraGroupId: groupId,
    EntraGroupDisplayName: group?.DisplayName
      || String(mapping.EntraGroupDisplayName || '').trim(),
    RoleCodes: [...new Set(
      (Array.isArray(mapping.RoleCodes) ? mapping.RoleCodes : [])
        .map((role) => String(role).trim())
        .filter(Boolean),
    )],
  };
};

export const upsertGroupRoleMapping = (
  mappings = [],
  mapping,
  groups = [],
) => {
  const normalized = normalizeGroupRoleMappingDraft(mapping, groups);
  const existing = (Array.isArray(mappings) ? mappings : [])
    .filter((item) => (
      String(item.UniqueId) !== String(normalized.UniqueId)
      && String(item.EntraGroupId) !== normalized.EntraGroupId
    ));
  return [...existing, normalized];
};

export const removeGroupRoleMapping = (mappings = [], mappingId) => (
  (Array.isArray(mappings) ? mappings : [])
    .filter((mapping) => String(mapping.UniqueId) !== String(mappingId))
);

export const canExecuteOnboarding = (run) => (
  run?.RunState === 'Validated'
);

export const canResumeOnboarding = (run) => (
  run?.RunState === 'Blocked' || run?.RunState === 'Failed'
);

export const canCancelOnboarding = (run) => (
  !!run
  && !['Running', 'Completed', 'Cancelled'].includes(run.RunState)
);
